import * as React from "react";
import { motion, useAnimationControls } from "framer-motion";
import { TrendingUp, Zap, Users } from "lucide-react";

const LeaderboardLoader = ({ loadingComplete }) => {
  const floatingIcons = [
    { Icon: TrendingUp, delay: 0, color: "text-emerald-500" },
    { Icon: Zap, delay: 0.15, color: "text-blue-500" },
    { Icon: Users, delay: 0.3, color: "text-purple-500" },
  ];

  const [secondsLeft, setSecondsLeft] = React.useState(60);

  React.useEffect(() => {
    let interval;

    if (!loadingComplete) {
      setSecondsLeft(60);
      interval = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setSecondsLeft(0);
    }

    return () => clearInterval(interval);
  }, [loadingComplete]);

  const controls = useAnimationControls();

  React.useEffect(() => {
    controls.set({ width: "0%" });
  }, [controls]);

  React.useEffect(() => {
    if (loadingComplete) {
      controls.start({
        width: "100%",
        transition: { duration: 0.6, ease: "easeOut" },
      });
      return;
    }

    controls.start({
      width: ["0%", "100%"],
      transition: { duration: 60, ease: "linear" },
    });
  }, [loadingComplete,controls]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: loadingComplete ? 0 : 1 }}
      transition={{ duration: 0.6 }}
      className="flex items-center justify-center bg-transparent backdrop-blur-md"
    >
      <div className="relative w-full">
        {/* Background Orbs */}
        <motion.div
          className="absolute top-0 left-1/4 w-56 h-56 bg-blue-400/20 rounded-full blur-3xl"
          animate={{ scale: [1, 1.05, 1], opacity: [0.25, 0.35, 0.25] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 right-1/4 w-56 h-56 bg-emerald-400/20 rounded-full blur-3xl"
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.35, 0.25, 0.35] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-12"
        >
          {/* Floating Icons */}
          <div className="absolute inset-0 overflow-hidden rounded-3xl">
            {floatingIcons.map(({ Icon, delay, color }, index) => (
              <motion.div
                key={index}
                className={`absolute ${color}`}
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: [0, 0.25, 0], scale: [0.8, 1, 0.8] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  delay,
                  ease: "easeInOut",
                }}
                style={{
                  left: `${20 + index * 20}%`,
                  top: `${35 + index * 12}%`,
                }}
              >
                <Icon className="w-7 h-7" />
              </motion.div>
            ))}
          </div>

          {/* MAIN CONTENT */}
          <div className="relative z-10 flex flex-col items-center gap-8">
            {/* Premium Pulse Ring Loader */}
            {/* Futuristic 3D Spinning Cube Loader */}
            <div className="relative flex items-center justify-center h-20 w-20">
              {/* Orbiting Dots */}
              {[0, 1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  className="absolute w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500"
                  animate={{
                    rotate: 360,
                  }}
                  transition={{
                    duration: 4 + i,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  style={{
                    transformOrigin: "30px 30px",
                    top: "50%",
                    left: "50%",
                  }}
                />
              ))}

              {/* Central Rotating Cube */}
              <motion.div
                className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 shadow-2xl rounded-lg"
                animate={{
                  rotateY: [0, 180, 360],
                  rotateX: [0, 180, 360],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
            </div>

            {/* Heading */}
            <motion.h1
              className="text-4xl md:text-5xl h-14 font-bold bg-gradient-to-r from-blue-900 via-purple-900 to-indigo-900 bg-clip-text text-transparent text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Loading Battlefield Analytics…
            </motion.h1>

            {/* Description */}
            <motion.p
              className="text-gray-600 text-lg text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Collecting power stats, examining your last moves, and
              recalibrating the leaderboard.
            </motion.p>

            {/* Progress Bar */}
            <div className="w-full max-w-md space-y-3">
              <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-purple-500"
                  initial={{ width: "0%" }}
                  animate={controls}
                />
              </div>
              <div className="flex justify-between items-center px-1">
                <p className="text-xs text-gray-500 font-medium">
                  Loading time left:
                </p>
                <p className="text-sm font-semibold text-gray-700">
                  {secondsLeft}s
                </p>
              </div>

              <motion.div
                className="flex items-center justify-center gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                    animate={{ scale: [1, 1.4, 1] }}
                    transition={{
                      duration: 1.4,
                      repeat: Infinity,
                      delay: i * 0.25,
                    }}
                  />
                ))}
              </motion.div>
            </div>

            <motion.p
              className="text-sm text-gray-500 font-medium mt-2"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2.5, repeat: Infinity }}
            >
              Stand by… optimizing your leaderboard insights.
            </motion.p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LeaderboardLoader;
